import { Input, Form, Select, Card, message, Alert, Typography, Tabs, Flex, Button } from 'antd';
import {
  CheckCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  RobotOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import AI from '../../../domain/models/AI';
import AIGender, { aiGenderAsString } from '../../../domain/enums/ai/AIGender';
import { EAIReminderTiming } from '@leedpulse-monorepo/enums';
import { renderReminderTimingOption } from '@leedpulse-monorepo/enums-ui';
import { FormCard, FormSectionTitle, FormContainer, FormLabelWithHelp, READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import { useContext, useEffect, useState } from 'react';
import AIEditionFormContext from '../../contexts/forms/ai/AIEditionFormContext';
import TextArea from 'antd/es/input/TextArea';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import { UserRole, AIStatus, ContextStatus, EProductType } from '@leedpulse-monorepo/enums';
import DomainsContext from '../../contexts/data/DomainsContext';
import AITon, { renderTonOption } from '../../../domain/enums/ai/AITon';
import AIVoice, { aiVoiceAsString } from '../../../domain/enums/ai/AIVoice';
import getVoicePreviewUrl from '../../../domain/use_cases/ai/getVoicePreviewUrl';
import AILanguage, { aiLanguageAsString } from '../../../domain/enums/ai/AILanguage';
import AIReceptionistCatalogEditor from './AIReceptionistCatalogEditor';
import getSubscriptionsByIds from '../../../domain/use_cases/subscriptions/getSubscriptionsByIds';
import getPlansRefs from '../../../domain/use_cases/plans/getPlansRefs';
import DomainsSelect from '../domains_page/select/DomainsSelect';

interface AIDetailProps {
  ai: AI;
  subscriptionId: string;
}

const AIDetail: React.FC<AIDetailProps> = ({ ai, subscriptionId }) => {
  const aiEditionFormContext = useContext(AIEditionFormContext);
  const domainsContext = useContext(DomainsContext);

  const authContext = useContext(AuthenticationContext);

  useEffect(() => {
    if (aiEditionFormContext.wipAI === null) {
      aiEditionFormContext.initWithAI(ai);
    }
  }, []);

  // The domain only changes on Leedflow: on Leedpulse the SMS scenarios of the old domain would stay
  const [isLeedflow, setIsLeedflow] = useState(false);
  useEffect(() => {
    Promise.all([getSubscriptionsByIds([subscriptionId]), getPlansRefs({ product: EProductType.LEEDFLOW })])
      .then(([[subscription], { plansRefs }]) => setIsLeedflow(plansRefs.some((plan) => plan.id === subscription?.idPlan)))
      .catch(() => setIsLeedflow(false));
  }, [subscriptionId]);

  useEffect(() => {
    switch (aiEditionFormContext.status) {
      case ContextStatus.SUCCESS:
        message.success("Mise à jour de l'IA avec succès");
        break;
      case ContextStatus.ERROR:
        message.error("Erreur lors de la mise à jour de l'IA");
        break;
      default:
        break;
    }
  }, [aiEditionFormContext.status]);

  const isBusy = aiEditionFormContext.status === ContextStatus.LOADING;
  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );
  const isOwner = authContext.authentication?.role === UserRole.owner;

  const wipAI = aiEditionFormContext.wipAI;
  // The domains are loaded by the DomainsSelect of the first tab
  const aiDomain = domainsContext.items.find((domain) => domain.id === wipAI?.domain);

  const [voicePreview, setVoicePreview] = useState<{ voiceId: string; url: string } | null>(null);
  const [voicePreviewLoading, setVoicePreviewLoading] = useState(false);
  const [voicePreviewError, setVoicePreviewError] = useState(false);

  // No voice picked: the backend falls back to Emilie (DEFAULT_VOICE_ID)
  const currentVoiceId = wipAI?.aiReceptionist?.voice_id || AIVoice.emilie;
  const isVoicePreviewCurrent = voicePreview !== null && voicePreview.voiceId === currentVoiceId;

  const loadVoicePreview = async () => {
    if (!currentVoiceId) return;
    setVoicePreviewLoading(true);
    setVoicePreviewError(false);
    try {
      const url = await getVoicePreviewUrl(authContext.authentication, currentVoiceId);
      setVoicePreview({ voiceId: currentVoiceId, url });
    } catch {
      setVoicePreviewError(true);
      message.error("Impossible de charger l'aperçu de cette voix.");
    } finally {
      setVoicePreviewLoading(false);
    }
  };

  return wipAI === null ? (
    <Card loading />
  ) : (
    <FormCard
      title={wipAI.id}
      onUpdated={
        isReadOnly ? undefined : () => aiEditionFormContext.submitUpdate()
      }
    >
      <Tabs defaultActiveKey="1">
        <Tabs.TabPane key="1" tab={<Flex align="center"><RobotOutlined style={{ marginRight: 6 }} /> IA Leedpulse</Flex>}>
          <Form layout="vertical" requiredMark={false}>
            <FormSectionTitle title="Informations générales" />
            <FormContainer>
              <Form.Item label="Nom de l'agent">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onFullnameChange(e.target.value);
                  }}
                  value={wipAI.fullname}
                />
              </Form.Item>
              <Form.Item label="Genre">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onGenderChange(value as AIGender);
                  }}
                  value={wipAI.gender}
                >
                  {Object.keys(AIGender).map((key) => (
                    <Select.Option
                      key={key}
                      value={AIGender[key as keyof typeof AIGender]}
                    >
                      {aiGenderAsString(AIGender[key as keyof typeof AIGender])}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label={<FormLabelWithHelp label="Domaine" help="Réservé aux owners, sur un plan Leedflow. Changer de domaine vide les activités et les motifs de la réceptionniste." />}>
                <DomainsSelect
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly || !isOwner || !isLeedflow}
                  onChange={(value) => {
                    aiEditionFormContext.onDomainChange(value as string);
                  }}
                  value={wipAI.domain}
                />
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Détails" />
            <FormContainer>
              <Form.Item label="Nom commercial">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onNameChange(e.target.value);
                  }}
                  value={wipAI.name}
                />
              </Form.Item>
              <Form.Item label="Localisation">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onLocationChange(e.target.value);
                  }}
                  value={wipAI.location}
                />
              </Form.Item>
              <Form.Item
                label="Fonction"
                tooltip="Aussi utilisée par le réceptionniste IA téléphonique pour se présenter, si le champ Intro ci-dessous est vide."
              >
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onProfileChange(e.target.value);
                  }}
                  value={wipAI.profile}
                />
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item label="Site web">
                <Input
                  placeholder="Ex: https://www.example.com"
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onWebsiteChange(e.target.value);
                  }}
                  value={wipAI.website}
                />
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Intro"
                tooltip="Aussi utilisée par le réceptionniste IA téléphonique pour se présenter si besoin lors d'un appel."
              >
                <TextArea
                  rows={8}
                  minLength={500}
                  maxLength={10000}
                  showCount
                  placeholder="Une description longue de la structure et de ses services. (500-1200 caractères recommandés)"
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onIntroChange(e.target.value);
                  }}
                  value={wipAI.intro}
                />
              </Form.Item>
            </FormContainer>
            <FormContainer twoColumnsMax>
              <Form.Item label="Action">
                <TextArea
                  rows={8}
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  showCount
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onActionChange(e.target.value);
                  }}
                  value={wipAI.action}
                />
              </Form.Item>
              <Form.Item label="Comportement">
                <TextArea
                  rows={8}
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  showCount
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onBehaviourChange(e.target.value);
                  }}
                  value={wipAI.behaviour}
                />
              </Form.Item>
              <Form.Item label="Cible">
                <TextArea
                  rows={8}
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  showCount
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onTargetChange(e.target.value);
                  }}
                  value={wipAI.target}
                />
              </Form.Item>
              <Form.Item label="Contraintes">
                <TextArea
                  rows={8}
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  showCount
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onConstraintsChange(e.target.value);
                  }}
                  value={wipAI.constraints}
                />
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Paramètres" />
            <FormContainer>
              <Form.Item label="Etat de l'IA">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onStatusChange(value as AIStatus);
                  }}
                  value={wipAI.status}
                >
                  <Select.Option value={AIStatus.pausedFull}>
                    <StopOutlined style={{ marginRight: 6 }} />
                    En pause
                  </Select.Option>
                  <Select.Option value={AIStatus.pausedNewcontacts}>
                    <PauseCircleOutlined style={{ marginRight: 6 }} />
                    En pause partielle (nouveaux contacts non engagés)
                  </Select.Option>
                  <Select.Option value={AIStatus.active}>
                    <CheckCircleOutlined style={{ marginRight: 6 }} />
                    Active
                  </Select.Option>
                </Select>
              </Form.Item>
              <Form.Item
                label="Type de langage"
                tooltip="Ce ton est aussi repris par le réceptionniste IA téléphonique de ce compte."
              >
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onTonChange(value);
                  }}
                  value={wipAI.ton}
                >
                  {Object.keys(AITon).map((key) => (
                    <Select.Option
                      key={key}
                      value={AITon[key as keyof typeof AITon]}
                    >
                      {renderTonOption(AITon[key as keyof typeof AITon])}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Timing des réponses" />
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Délai minimum pour répondre (minutes)"
                style={{ flex: 1 }}
              >
                <Input
                  type="number"
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onFirstReplyTimingChange(
                      Number(e.target.value)
                    );
                  }}
                  value={wipAI.replyTiming[0]}
                />
              </Form.Item>
              <Form.Item
                label="Délai maximum pour répondre (minutes)"
                style={{ flex: 1 }}
              >
                <Input
                  type="number"
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onSecondReplyTimingChange(
                      Number(e.target.value)
                    );
                  }}
                  value={wipAI.replyTiming[1]}
                />
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Timing des relances" />
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Délai avant envoi d'une 1ère relance"
                style={{ flex: 1 }}
              >
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onFirstReminderTimingChange(value);
                  }}
                  value={wipAI.reminderTiming[0]}
                >
                  {Object.keys(EAIReminderTiming).map((key) => (
                    <Select.Option
                      key={key}
                      value={Number(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    >
                      {renderReminderTimingOption(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Délai avant envoi d'une 2ème relance (relance exécutée X heures / jours après la 1ère)"
                style={{ flex: 1 }}
              >
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onSecondReminderTimingChange(value);
                  }}
                  value={wipAI.reminderTiming[1]}
                >
                  {Object.keys(EAIReminderTiming).map((key) => (
                    <Select.Option
                      key={key}
                      value={Number(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    >
                      {renderReminderTimingOption(
                        EAIReminderTiming[key as keyof typeof EAIReminderTiming]
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <FormSectionTitle title="Timing de prise de contact Leedflow" />
            <FormContainer>
              <Form.Item
                label="Délai avant envoi du premier message (minutes)"
                style={{ flex: 1 }}
              >
                <Input
                  type="number"
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  min={0}
                  onChange={(e) => {
                    aiEditionFormContext.onFlowTimingChange(
                      Number(e.target.value)
                    );
                  }}
                  value={wipAI.flowTiming}
                />
              </Form.Item>
            </FormContainer>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane key="2" tab={<Flex align="center"><PhoneOutlined style={{ marginRight: 6 }} /> Réceptionniste</Flex>}>
          <Form layout="vertical" requiredMark={false}>
            <FormSectionTitle title="Réceptionniste IA" />
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Message d'accueil"
                tooltip="Phrase prononcée par le réceptionniste IA téléphonique au décroché de l'appel. Laisser vide pour utiliser le message par défaut."
              >
                <TextArea
                  rows={3}
                  maxLength={500}
                  showCount
                  placeholder="Ex: Bonjour, je suis {{assistant_name}}, l'assistante de {{public_label}}."
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onFirstMessageChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.first_message}
                />
              </Form.Item>
              <Form.Item
                label="Critères de qualification"
                tooltip="Informations que le réceptionniste IA cherche à récupérer pendant l'appel (motif, typologie, urgence, etc.). Laisser vide pour utiliser les critères par défaut."
              >
                <TextArea
                  rows={6}
                  maxLength={1000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onQualificationCriteriaChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.qualification_criteria}
                />
              </Form.Item>
              <Form.Item
                label="Voix"
                tooltip="Voix ElevenLabs utilisée par le réceptionniste IA téléphonique. Emilie par défaut."
              >
                <Flex gap={8} align="center">
                  <Select
                    style={{ flex: 1, ...(isReadOnly ? READ_ONLY_INPUT_STYLE : {}) }}
                    disabled={isBusy || isReadOnly}
                    onChange={(value) => {
                      aiEditionFormContext.onVoiceIdChange(value as AIVoice | undefined);
                    }}
                    value={currentVoiceId}
                  >
                    {Object.keys(AIVoice).map((key) => (
                      <Select.Option
                        key={key}
                        value={AIVoice[key as keyof typeof AIVoice]}
                      >
                        {aiVoiceAsString(AIVoice[key as keyof typeof AIVoice])}
                      </Select.Option>
                    ))}
                  </Select>
                  {isVoicePreviewCurrent ? (
                    <audio controls autoPlay src={voicePreview.url} style={{ height: 32 }} />
                  ) : (
                    <Button
                      size="small"
                      disabled={!currentVoiceId || isBusy}
                      loading={voicePreviewLoading}
                      onClick={loadVoicePreview}
                    >
                      {voicePreviewError ? 'Réessayer' : 'Écouter'}
                    </Button>
                  )}
                </Flex>
              </Form.Item>
              <Form.Item
                label="Langue"
                tooltip="Langue utilisée par le réceptionniste IA téléphonique. Laisser vide pour utiliser la langue par défaut (français)."
              >
                <Select
                  allowClear
                  placeholder="Langue par défaut"
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    aiEditionFormContext.onLanguageChange(value as AILanguage | undefined);
                  }}
                  value={wipAI.aiReceptionist?.language}
                >
                  {Object.keys(AILanguage).map((key) => (
                    <Select.Option
                      key={key}
                      value={AILanguage[key as keyof typeof AILanguage]}
                    >
                      {aiLanguageAsString(AILanguage[key as keyof typeof AILanguage])}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>Contexte de conversation</Typography.Title>
            <Alert
              type="warning"
              showIcon
              message="Si l'une des sections ci-dessous est renseignée, le prompt personnalisé défini au niveau du domaine sera ignoré pour cette IA."
              style={{ marginBottom: 16 }}
            />
            <FormContainer twoColumnsMax>
              <Form.Item
                label="Personnalité"
                tooltip="Remplace entièrement la section Personality par défaut. Laisser vide pour utiliser le texte par défaut. Pour conserver la présentation dynamique de l'IA, inclure {{personality_extra}} dans le texte."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onPersonalityChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.personality}
                />
              </Form.Item>
              <Form.Item
                label="Environnement"
                tooltip="Remplace entièrement la section Environment par défaut. Laisser vide pour utiliser le texte par défaut."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onEnvironmentChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.environment}
                />
              </Form.Item>
              <Form.Item
                label="Ton"
                tooltip="Remplace entièrement la section Tone par défaut. Laisser vide pour utiliser le texte par défaut. Pour conserver le réglage du ton de l'IA, inclure {{tone_instruction}} dans le texte."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onToneChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.tone}
                />
              </Form.Item>
              <Form.Item
                label="Objectif"
                tooltip="Remplace entièrement la section Goal par défaut. Laisser vide pour utiliser le texte par défaut. Pour conserver les critères de qualification saisis ci-dessus, inclure {{qualification_criteria}} dans le texte."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onGoalChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.goal}
                />
              </Form.Item>
              <Form.Item
                label="Clôture"
                tooltip="Remplace entièrement la section Closing par défaut. Laisser vide pour utiliser le texte par défaut."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onClosingChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.closing}
                />
              </Form.Item>
              <Form.Item
                label="Garde-fous"
                tooltip="Remplace entièrement la section Guardrails par défaut. Laisser vide pour utiliser le texte par défaut."
              >
                <TextArea
                  rows={6}
                  maxLength={2000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onGuardrailsChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.guardrails}
                />
              </Form.Item>
              <Form.Item
                label="Consignes additionnelles"
                tooltip="Instructions libres ajoutées à la toute fin du prompt, en complément des sections existantes (pas un remplacement)."
              >
                <TextArea
                  rows={6}
                  maxLength={1000}
                  showCount
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    aiEditionFormContext.onOtherChange(e.target.value);
                  }}
                  value={wipAI.aiReceptionist?.other}
                />
              </Form.Item>
            </FormContainer>

            <FormSectionTitle title="Activités et motifs" />
            <AIReceptionistCatalogEditor
              availableActivities={aiDomain?.ai_receptionist?.activities ?? []}
              availableMotifs={aiDomain?.ai_receptionist?.motifs ?? []}
              activities={wipAI.aiReceptionist?.activities ?? []}
              motifs={wipAI.aiReceptionist?.motifs ?? []}
              onActivitiesChange={aiEditionFormContext.onActivitiesChange}
              onMotifsChange={aiEditionFormContext.onMotifsChange}
              disabled={isBusy || isReadOnly}
            />
          </Form>
        </Tabs.TabPane>
      </Tabs>
    </FormCard>
  );
};

export default AIDetail;
